import React, { useCallback, useEffect, useState } from "react";
import { cmApi } from "./cmApi";

// BM-H4201 — ongoing competence activity log. One person's own record for
// one competence year (1 Nov to 31 Oct). For a CLC lawyer from 1 Nov 2026 it
// measures the year against 8 activities, at least 4 assessed, each with
// impact and outcome statements. For a solicitor it is a record only.

const EMPTY = {
  date: "", title: "", activity_type: "Course or webinar", assessed: false,
  assessment: "", mandatory_topic: "", impact: "", outcome: "", evidence_link: "",
};

function errorText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

function Progress({ requirement, summary }) {
  if (!requirement?.counted) {
    return (
      <p className="mt-2 text-sm text-slate-600" data-testid="cm-competence-basis">
        {requirement?.basis}
      </p>
    );
  }
  const met = summary.status === "met";
  return (
    <div className="mt-3" data-testid="cm-competence-progress">
      <p className="text-sm text-slate-700">
        {summary.activities} of {requirement.activities} activities logged,{" "}
        {summary.assessed} of {requirement.assessed} assessed. Return due {requirement.return_due}.
      </p>
      <p
        className={`mt-1 text-sm font-medium ${met ? "text-green-700" : "text-amber-700"}`}
        data-testid="cm-competence-status"
      >
        {met
          ? "Requirement met for this year."
          : `Still needed: ${summary.short_by.activities} more activit${summary.short_by.activities === 1 ? "y" : "ies"}` +
            (summary.short_by.assessed ? `, ${summary.short_by.assessed} more assessed` : "") +
            ". Only activities with both statements count."}
      </p>
    </div>
  );
}

export default function CompetenceLog() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    cmApi
      .competenceMe(year)
      .then((d) => { setData(d); setError(""); })
      .catch((e) => setError(errorText(e, "Could not load your competence log.")));
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await cmApi.addCompetenceActivity(form);
      setForm(EMPTY);
      load();
    } catch (err) {
      setError(errorText(err, "Could not save the activity."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await cmApi.deleteCompetenceActivity(id);
      load();
    } catch (err) {
      setError(errorText(err, "Could not delete the activity."));
    }
  };

  const changeRegulator = async (e) => {
    try {
      await cmApi.setCompetenceRegulator(e.target.value);
      load();
    } catch (err) {
      setError(errorText(err, "Could not change your regulator."));
    }
  };

  const requirement = data?.requirement;
  const summary = data?.summary;
  const activities = data?.activities || [];
  const missing = new Set(summary?.missing_statement_ids || []);

  return (
    <div className="p-6 max-w-4xl" data-testid="cm-competence-log">
      <h1 className="text-xl font-semibold text-slate-800">My competence log</h1>
      {error && <p className="mt-4 text-sm text-red-600" data-testid="cm-competence-error">{error}</p>}

      {data && (
        <section className="mt-4 rounded border border-slate-200 p-4">
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-700">
            <span>
              Year <strong data-testid="cm-competence-year">{data.year}</strong> ({data.starts} to {data.ends})
            </span>
            <button
              type="button"
              className="text-indigo-700 hover:underline"
              onClick={() => setYear(data.year_start - 1)}
            >
              Previous year
            </button>
            {year !== null && (
              <button type="button" className="text-indigo-700 hover:underline" onClick={() => setYear(null)}>
                This year
              </button>
            )}
            <label className="ml-auto">
              Regulator{" "}
              <select
                value={requirement?.regulator || "SRA"}
                onChange={changeRegulator}
                className="rounded border border-slate-300 px-2 py-1"
                data-testid="cm-competence-regulator"
              >
                <option value="SRA">SRA</option>
                <option value="CLC">CLC</option>
              </select>
            </label>
          </div>
          <Progress requirement={requirement} summary={summary} />
          {requirement?.source && (
            <a href={requirement.source} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-slate-500 underline">
              Regulator's guidance
            </a>
          )}
        </section>
      )}

      <section className="mt-6">
        <h2 className="text-base font-semibold text-slate-800">Activities</h2>
        {data && activities.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">Nothing logged for this year yet.</p>
        )}
        <ul className="mt-2 space-y-2">
          {activities.map((a) => (
            <li key={a._id} className="rounded border border-slate-200 px-4 py-3" data-testid="cm-competence-row">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-slate-800">{a.title}</div>
                  <div className="text-xs text-slate-500">
                    {a.date} · {a.activity_type}
                    {a.assessed ? ` · Assessed: ${a.assessment}` : " · Not assessed"}
                    {a.mandatory_topic ? ` · Mandatory topic: ${a.mandatory_topic}` : ""}
                  </div>
                  {missing.has(a._id) && (
                    <div className="mt-1 text-xs text-amber-700" data-testid="cm-competence-missing">
                      Add the impact and outcome statements for this to count.
                    </div>
                  )}
                </div>
                <button type="button" className="text-xs text-slate-500 hover:text-red-600" onClick={() => remove(a._id)}>
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-3 rounded border border-slate-200 p-4 sm:grid-cols-2" data-testid="cm-competence-form">
        <h2 className="sm:col-span-2 text-base font-semibold text-slate-800">Log an activity</h2>
        <label className="text-sm text-slate-700">Date
          <input type="date" required value={form.date} onChange={set("date")} className="mt-1 block w-full rounded border border-slate-300 px-2 py-1" />
        </label>
        <label className="text-sm text-slate-700">Type
          <select value={form.activity_type} onChange={set("activity_type")} className="mt-1 block w-full rounded border border-slate-300 px-2 py-1">
            {(data?.activity_types || [EMPTY.activity_type]).map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
        <label className="sm:col-span-2 text-sm text-slate-700">What it was
          <input required value={form.title} onChange={set("title")} className="mt-1 block w-full rounded border border-slate-300 px-2 py-1" />
        </label>
        <label className="text-sm text-slate-700 flex items-center gap-2">
          <input type="checkbox" checked={form.assessed} onChange={set("assessed")} /> Assessed
        </label>
        {form.assessed && (
          <label className="text-sm text-slate-700">How it was assessed
            <input required value={form.assessment} onChange={set("assessment")} className="mt-1 block w-full rounded border border-slate-300 px-2 py-1" />
          </label>
        )}
        <label className="sm:col-span-2 text-sm text-slate-700">Mandatory topic covered (if any)
          <input value={form.mandatory_topic} onChange={set("mandatory_topic")} className="mt-1 block w-full rounded border border-slate-300 px-2 py-1" />
        </label>
        <label className="sm:col-span-2 text-sm text-slate-700">Impact: what you learned and how it changes your practice
          <textarea value={form.impact} onChange={set("impact")} rows={2} className="mt-1 block w-full rounded border border-slate-300 px-2 py-1" />
        </label>
        <label className="sm:col-span-2 text-sm text-slate-700">Outcome: how you have applied it
          <textarea value={form.outcome} onChange={set("outcome")} rows={2} className="mt-1 block w-full rounded border border-slate-300 px-2 py-1" />
        </label>
        <label className="sm:col-span-2 text-sm text-slate-700">Link to evidence (optional)
          <input value={form.evidence_link} onChange={set("evidence_link")} className="mt-1 block w-full rounded border border-slate-300 px-2 py-1" />
        </label>
        <div className="sm:col-span-2">
          <button type="submit" disabled={saving} className="rounded bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50" data-testid="cm-competence-save">
            {saving ? "Saving…" : "Save activity"}
          </button>
        </div>
      </form>
    </div>
  );
}
